import { useForm } from "hooks/useForm";
import { Box, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { UserRole, getUserRoleName, USER_ROLES, User } from "features/auth/types/user";
import { useApproveUserMutation } from "app/api/users";
import { useGetAllCompaniesQuery } from "app/api/companies";
import SimpleDialog from "components/SimpleDialog";

type ApproveUserDialogProps = {
  user: User;
  onClose: () => void;
};

const ApproveUserDialog: React.FC<ApproveUserDialogProps> = (props) => {
  const { user: userToApprove, onClose } = props;
  const { data: companies } = useGetAllCompaniesQuery();

  const [approveUser] = useApproveUserMutation();

  const { handleSubmit, handleChange, data, errors } = useForm<{
    role: UserRole;
    company: string;
  }>({
    initialValues: {},
    onSubmit: () => {
      approveUser({
        userId: userToApprove.id,
        role: data.role,
        companyId: data.role === UserRole.EXTERNAL_INSTALLER ? data.company : null,
      })
        .then(() => {
          toast.success(`Användaren har blivit godkänd!`);
          onClose();
        })
        .catch(() => {
          toast.error(`Något gick fel vid godkännande av användaren.`);
        });
    },
    validations: {
      role: {
        required: {
          value: true,
          message: "Du måste välja en användarroll",
        },
      },
      company: {
        custom: {
          isValid: () => !(data.role === UserRole.EXTERNAL_INSTALLER && !data.company),
          message: "Du måste välja ett företag för externa installatörer",
        },
      },
    },
  });

  return (
    <SimpleDialog onClose={onClose} onSubmit={handleSubmit} title="Godkänn användare">
      <Box sx={{ display: "grid", gap: 1 }}>
        <TextField
          select
          variant="filled"
          label="Roll"
          error={!!errors.role}
          helperText={errors.role}
          value={data.role}
          onChange={handleChange("role")}
        >
          {USER_ROLES.filter((role) => role !== UserRole.NONE).map((role) => (
            <MenuItem key={role} value={role}>
              {getUserRoleName(role)}
            </MenuItem>
          ))}
        </TextField>

        {data.role === "EXTERNAL_INSTALLER" && (
          <TextField
            select
            variant="filled"
            label="Företag"
            error={!!errors.company}
            helperText={errors.company}
            value={data.company}
            onChange={handleChange("company")}
          >
            {companies?.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
    </SimpleDialog>
  );
};

export default ApproveUserDialog;
